import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchSession } from '../../store/slices/votingSlice';
import CountdownTimer from './CountdownTimer';
import VotingForm from './VotingForm';
import VotingResults from './VotingResults';
import InlineEditVotingSession from '../UI/InlineEdit/InlineEditVotingSession';

interface VotingSession {
  sessionId: string;
  sessionNumber: number;
  type: 'open' | 'anonymous';
  question: string;
  status: 'scheduled' | 'active' | 'completed' | 'canceled';
  startDate: string;
  endDate: string;
  connectedGoals?: string[];
  connectedMilestones?: string[];
  connectedDocuments?: string[];
  results?: {
    totalVotes: number;
    yes: number;
    no: number;
    abstain: number;
    byParish?: Array<{
      parish: string;
      yes: number;
      no: number;
      abstain: number;
    }>;
  };
}

interface VotingSessionCardProps {
  session: VotingSession;
  projectId: string;
  isOwner?: boolean;
  onUpdate?: (sessionId: string, updates: Partial<VotingSession>) => Promise<void>;
}

const VotingSessionCard: React.FC<VotingSessionCardProps> = ({ session, projectId, isOwner = false, onUpdate }) => {
  const dispatch = useAppDispatch();
  const { sessions } = useAppSelector((state) => state.voting);
  const sessionData = sessions[session.sessionId];

  // Fetch session data if not already loaded
  useEffect(() => {
    if (!sessionData) {
      dispatch(fetchSession({ projectId, sessionId: session.sessionId }));
    }
  }, [dispatch, projectId, session.sessionId, sessionData]);

  const isActive = session.status === 'active';
  const now = new Date();
  const startDate = new Date(session.startDate);
  const endDate = new Date(session.endDate);
  const isScheduled = session.status === 'scheduled' && now < startDate;
  const isInTimeRange = now >= startDate && now <= endDate;
  const isCompleted = session.status === 'completed' || now > endDate;

  const canVote = isActive && isInTimeRange && !sessionData?.userVote;

  return (
    <div className="bg-[var(--color-default-background)] border border-[var(--color-neutral-border)] rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-[var(--color-default-font)]">
            Voting Session {session.sessionNumber}
          </h3>
          <p className="text-sm text-[var(--color-subtext-color)] mt-1">
            {session.type === 'open' ? 'Open Voting' : 'Anonymous Voting'}
          </p>
        </div>
        <span
          className={`px-3 py-1 text-sm font-medium rounded ${
            canVote
              ? 'bg-[var(--color-success-100)] text-[var(--color-success-800)]'
              : isCompleted
              ? 'bg-[var(--color-neutral-100)] text-[var(--color-neutral-800)]'
              : isScheduled
              ? 'bg-[var(--color-warning-100)] text-[var(--color-warning-800)]'
              : 'bg-[var(--color-brand-100)] text-[var(--color-brand-800)]'
          }`}
        >
          {session.status}
        </span>
      </div>

      {isOwner && session.status === 'scheduled' && onUpdate ? (
        <div className="mb-4">
          <InlineEditVotingSession
            session={session}
            onSave={onUpdate}
            disabled={!isOwner}
            showEditButton={isOwner}
          />
        </div>
      ) : (
        <p className="text-[var(--color-default-font)] mb-4">{session.question}</p>
      )}

      {/* Countdown before start */}
      {isScheduled && (
        <div className="mb-4">
          <CountdownTimer targetDate={session.startDate} />
        </div>
      )}

      <div className="text-sm text-[var(--color-subtext-color)] mb-4">
        <p>Start: {new Date(session.startDate).toLocaleString()}</p>
        <p>End: {new Date(session.endDate).toLocaleString()}</p>
      </div>

      {/* Connections */}
      {(session.connectedGoals?.length || session.connectedMilestones?.length || session.connectedDocuments?.length) && (
        <div className="mb-4 p-3 bg-[var(--color-neutral-50)] rounded-lg">
          <p className="text-xs font-medium text-[var(--color-default-font)] mb-1">Connected to:</p>
          <div className="flex flex-wrap gap-2">
            {session.connectedGoals?.length && (
              <span className="text-xs bg-[var(--color-brand-100)] text-[var(--color-brand-700)] px-2 py-1 rounded">
                {session.connectedGoals.length} Goal(s)
              </span>
            )}
            {session.connectedMilestones?.length && (
              <span className="text-xs bg-[var(--color-brand-200)] text-[var(--color-brand-800)] px-2 py-1 rounded">
                {session.connectedMilestones.length} Milestone(s)
              </span>
            )}
            {session.connectedDocuments?.length && (
              <span className="text-xs bg-[var(--color-success-100)] text-[var(--color-success-700)] px-2 py-1 rounded">
                {session.connectedDocuments.length} Document(s)
              </span>
            )}
          </div>
        </div>
      )}

      {/* User has already voted - Show vote information instead of form */}
      {sessionData?.userVote && (
        <div className="mb-4 p-4 bg-[var(--color-success-50)] border border-[var(--color-success-200)] rounded-lg">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-[var(--color-success-700)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-[var(--color-success-800)] mb-1">
                You have already voted
              </p>
              <p className="text-base font-semibold text-[var(--color-success-900)] capitalize">
                Your vote: {sessionData.userVote.option}
              </p>
              {sessionData.userVote.castAt && (
                <p className="text-xs text-[var(--color-success-600)] mt-1">
                  Cast on {new Date(sessionData.userVote.castAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Voting Form - Only show if user hasn't voted */}
      {canVote && !sessionData?.userVote && (
        <div className="mb-4">
          <VotingForm
            projectId={projectId}
            sessionId={session.sessionId}
            sessionType={session.type}
            onVoteCast={() => {
              // Refresh session data after voting
              dispatch(fetchSession({ projectId, sessionId: session.sessionId }));
            }}
          />
        </div>
      )}

      {/* Results */}
      {sessionData?.results && (
        <div className="mt-4 pt-4 border-t">
          <VotingResults
            results={sessionData.results}
            voteDetails={sessionData.voteDetails}
            isAnonymous={session.type === 'anonymous'}
          />
        </div>
      )}
    </div>
  );
};

export default VotingSessionCard;

